import React, { useEffect, useMemo, useState } from 'react';
import SettingsTabs from '../layout/SettingsTabs';
import { getBusinessHours, saveBusinessHours } from '../../api/services/businessService';

const LABELS = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const NOTIFY = [['any_agent', 'Any available agent'], ['admins', 'Owners and Admins only']];

const strip = (doc) => ({ days: doc.days, offHours: doc.offHours, escalation: doc.escalation });

/** Same rules as PUT /api/business-hours, so most mistakes are caught before a request is sent. */
function validate(doc) {
  const e = {};
  doc.days.forEach((d) => {
    if (!d.open) return;
    if (!HHMM.test(d.from || '') || !HHMM.test(d.to || '')) e[`days.${d.day}`] = `${LABELS[d.day]}: enter both times, for example 09:00.`;
    else if (d.from >= d.to) e[`days.${d.day}`] = `${LABELS[d.day]}: closing time must be after opening time.`;
  });
  if (!doc.days.some((d) => d.open) && doc.offHours.mode !== 'offline') e.days = 'Open at least one day, or choose "Show an offline message" so visitors know you are closed.';
  if (doc.offHours.mode === 'offline') {
    const m = (doc.offHours.offlineMessage || '').trim();
    if (m.length < 10) e['offHours.offlineMessage'] = 'Offline message must be at least 10 characters.';
    else if (m.length > 300) e['offHours.offlineMessage'] = 'Offline message must be at most 300 characters.';
  }
  const w = doc.escalation.waitAlertMinutes;
  if (w === '' || !Number.isInteger(Number(w)) || Number(w) < 1 || Number(w) > 120) e['escalation.waitAlertMinutes'] = 'Wait-time alert must be a whole number from 1 to 120 minutes.';
  return e;
}

const Err = ({ id, text }) => (text ? <div id={id} className="text-danger small mt-1" role="alert">{text}</div> : null);

export default function BusinessHoursPage() {
  const [saved, setSaved] = useState(null);
  const [doc, setDoc] = useState(null);
  const [meta, setMeta] = useState(null);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState('loading'); // loading | ready | error
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [formError, setFormError] = useState('');

  const load = () => {
    setState('loading');
    getBusinessHours()
      .then((res) => { setSaved(strip(res)); setDoc(strip(res)); setMeta({ timezone: res.timezone, isOpenNow: res.isOpenNow, localTime: res.localTime }); setState('ready'); })
      .catch((err) => { setLoadError(err.message || 'Unable to load business hours.'); setState('error'); });
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => !!doc && JSON.stringify(doc) !== JSON.stringify(saved), [doc, saved]);

  const setDay = (day, patch) => { setNotice(''); setDoc((d) => ({ ...d, days: d.days.map((x) => (x.day === day ? { ...x, ...patch } : x)) })); };
  const setOff = (patch) => { setNotice(''); setDoc((d) => ({ ...d, offHours: { ...d.offHours, ...patch } })); };
  const setEsc = (patch) => { setNotice(''); setDoc((d) => ({ ...d, escalation: { ...d.escalation, ...patch } })); };
  const copyMonday = () => {
    const mon = doc.days.find((d) => d.day === 'mon');
    setNotice('');
    setDoc((d) => ({ ...d, days: d.days.map((x) => (['tue', 'wed', 'thu', 'fri'].includes(x.day) ? { ...x, open: mon.open, from: mon.from, to: mon.to } : x)) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError(''); setNotice('');
    const found = validate(doc);
    setErrors(found);
    if (Object.keys(found).length) { setFormError('Please fix the highlighted fields.'); return; }
    setBusy(true);
    try {
      const res = await saveBusinessHours({ ...doc, escalation: { ...doc.escalation, waitAlertMinutes: Number(doc.escalation.waitAlertMinutes) } });
      setSaved(strip(res)); setDoc(strip(res)); setMeta({ timezone: res.timezone, isOpenNow: res.isOpenNow, localTime: res.localTime });
      setNotice('Business hours saved.');
    } catch (err) {
      const fields = err.details && err.details.fields;
      if (fields) setErrors(fields);
      setFormError(err.message || 'Unable to save business hours.');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="h3 mb-1">Settings</h1>
      <p className="text-muted mb-3">When your team is available, and what happens when a visitor asks for a person.</p>
      <SettingsTabs />

      {state === 'loading' && <p className="text-muted" role="status">Loading business hours…</p>}
      {state === 'error' && <div className="alert alert-danger" role="alert">{loadError} <button type="button" className="btn btn-link p-0" onClick={load}>Retry</button></div>}

      {state === 'ready' && doc && (
        <form onSubmit={submit} noValidate className="row g-4" aria-label="Business hours and escalation settings">
          <div className="col-lg-7">
            <section className="card p-4 h-100" aria-labelledby="bh-hours">
              <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                <div>
                  <h2 className="h5 mb-1" id="bh-hours">Opening hours</h2>
                  <div className="text-muted small">Times are in {meta.timezone} (change it under Business settings). It is {meta.localTime} there now.</div>
                </div>
                <span className={`badge ${meta.isOpenNow ? 'text-bg-success' : 'text-bg-secondary'}`}>{meta.isOpenNow ? 'Open now' : 'Closed now'}</span>
              </div>
              {errors.days && <div className="alert alert-danger py-2" role="alert">{errors.days}</div>}
              <div className="d-grid gap-2">
                {doc.days.map((d) => (
                  <div key={d.day} className="row g-2 align-items-center">
                    <div className="col-12 col-xxl-4">
                      <div className="form-check form-switch">
                        <input id={`open-${d.day}`} className="form-check-input" type="checkbox" role="switch" checked={d.open} onChange={(e) => setDay(d.day, { open: e.target.checked })} />
                        <label className="form-check-label" htmlFor={`open-${d.day}`}>{LABELS[d.day]}</label>
                      </div>
                    </div>
                    {d.open ? (
                      <>
                        <div className="col-auto"><label className="visually-hidden" htmlFor={`from-${d.day}`}>{LABELS[d.day]} opens</label>
                          <input id={`from-${d.day}`} type="time" className={`form-control form-control-sm bh-time ${errors[`days.${d.day}`] ? 'is-invalid' : ''}`} value={d.from} onChange={(e) => setDay(d.day, { from: e.target.value })} /></div>
                        <div className="col-auto text-center text-muted px-0">to</div>
                        <div className="col-auto"><label className="visually-hidden" htmlFor={`to-${d.day}`}>{LABELS[d.day]} closes</label>
                          <input id={`to-${d.day}`} type="time" className={`form-control form-control-sm bh-time ${errors[`days.${d.day}`] ? 'is-invalid' : ''}`} value={d.to} onChange={(e) => setDay(d.day, { to: e.target.value })} /></div>
                      </>
                    ) : <div className="col text-muted small">Closed</div>}
                    <div className="col-12"><Err text={errors[`days.${d.day}`]} /></div>
                  </div>
                ))}
              </div>
              <div className="mt-3"><button type="button" className="btn btn-sm btn-outline-secondary" onClick={copyMonday}>Copy Monday to Tuesday–Friday</button></div>
            </section>
          </div>

          <div className="col-lg-5 d-grid gap-4 align-content-start">
            <section className="card p-4" aria-labelledby="bh-off">
              <h2 className="h5 mb-1" id="bh-off">When you are closed</h2>
              <p className="text-muted small">What a visitor sees if they ask for a human outside opening hours.</p>
              <div className="form-check"><input className="form-check-input" type="radio" name="off-mode" id="off-queue" checked={doc.offHours.mode === 'queue'} onChange={() => setOff({ mode: 'queue' })} />
                <label className="form-check-label" htmlFor="off-queue"><strong>Queue it</strong><div className="small text-muted">They are told an agent will reply when you reopen. The chat waits in the Escalations tab.</div></label></div>
              <div className="form-check mt-2"><input className="form-check-input" type="radio" name="off-mode" id="off-offline" checked={doc.offHours.mode === 'offline'} onChange={() => setOff({ mode: 'offline' })} />
                <label className="form-check-label" htmlFor="off-offline"><strong>Show an offline message</strong><div className="small text-muted">They see your own message instead.</div></label></div>
              {doc.offHours.mode === 'offline' && (
                <div className="mt-3">
                  <label className="form-label d-flex justify-content-between" htmlFor="off-message"><span>Offline message</span><span className="text-muted small">{(doc.offHours.offlineMessage || '').length} / 300</span></label>
                  <textarea id="off-message" rows={3} className={`form-control ${errors['offHours.offlineMessage'] ? 'is-invalid' : ''}`} value={doc.offHours.offlineMessage} onChange={(e) => setOff({ offlineMessage: e.target.value })} />
                  <Err text={errors['offHours.offlineMessage']} />
                </div>
              )}
              <Err text={errors['offHours.mode']} />
            </section>

            <section className="card p-4" aria-labelledby="bh-esc">
              <h2 className="h5 mb-1" id="bh-esc">Escalation</h2>
              <div className="mb-3">
                <label className="form-label" htmlFor="esc-wait">Alert when a visitor waits longer than (minutes)</label>
                <input id="esc-wait" type="number" min="1" max="120" className={`form-control ${errors['escalation.waitAlertMinutes'] ? 'is-invalid' : ''}`} value={doc.escalation.waitAlertMinutes} onChange={(e) => setEsc({ waitAlertMinutes: e.target.value })} />
                <Err text={errors['escalation.waitAlertMinutes']} />
              </div>
              <div>
                <label className="form-label" htmlFor="esc-notify">Notify about new escalations</label>
                <select id="esc-notify" className="form-select" value={doc.escalation.notify} onChange={(e) => setEsc({ notify: e.target.value })}>
                  {NOTIFY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <div className="text-muted small mt-1">Controls who is notified. Every agent can still see the Escalations tab.</div>
                <Err text={errors['escalation.notify']} />
              </div>
            </section>
          </div>

          <div className="col-12">
            {formError && <div className="alert alert-danger py-2" role="alert">{formError}</div>}
            {notice && <div className="alert alert-success py-2" role="status">{notice}</div>}
            <div className="d-flex gap-2">
              <button className="btn btn-primary" type="submit" disabled={busy || !dirty}>{busy ? 'Saving…' : 'Save changes'}</button>
              <button className="btn btn-outline-secondary" type="button" disabled={busy || !dirty} onClick={() => { setDoc(saved); setErrors({}); setFormError(''); }}>Discard changes</button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
